/**
 * AI先輩の答え1件の画面（issue #109・#185）。
 *
 * ここでできること（docs/01 11-6・11-7）:
 *  - 元の投稿（誰が・どの部屋・いつ・添えられた写真）を読みながら、**質問と答えを直す**
 *    （写真は承認する方が見るためのもので、AI先輩の材料になるのは文字だけ・決定26）
 *  - **承認する**（ここを通って初めて、AI先輩の回答に使われます）
 *  - **使わない／取り下げる**（以後は参照されません）
 *
 * 元の投稿が取り消された・保存期間で消えたときは、承認される前の下書きの文も消してある（決定28）。
 * その1件を開いたときは、直す欄と「承認する」を出さず、なぜ文が無いのかと次の手を言葉で出す。
 * 他の拠点の方の投稿でも、写真は出す（伏せるのはお名前と部屋の名前だけ・決定29）。
 *
 * 決めごと（docs/06 原則1・2・4）:
 *  - 直した内容が残るのは「保存」を押したときだけ。押したら結果を必ず出す
 *  - 承認・取り下げの前には**確認の窓**をはさみ、終わったら結果を出す
 *  - 状態は言葉で出す（「承認待ち」「使われています」「取り下げ済み」）
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError, messageForStatus } from '../api/client.js';
import { useApi } from '../api/context.js';
import type { AiAttachmentLink, AiKnowledgeItem, AiSourceAttachment } from '../api/types.js';
import {
  knowledgeKindLabel,
  knowledgeStatusLabel,
  knowledgeStatusNote,
  pendingKindLabel,
  pendingKindOf,
  sourceNotes,
  sourceTextOf,
  whoAndWhere,
} from '../ai/labels.js';
import { formatDateTime } from '../members/labels.js';
import { ErrorBlock, LoadingBlock } from '../components/StatusBlock.js';
import { loadFailure, type LoadFailure, nextStepFor } from '../api/load-failure.js';
import { Button } from '../components/ui/Button.js';
import { Card } from '../components/ui/Card.js';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.js';
import { FormCard } from '../components/ui/FormCard.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { ResultBanner } from '../components/ui/ResultBanner.js';
import { withReason } from '../format/sentence.js';

/** いま確認の窓を出している操作。 */
type Confirming = 'none' | 'approve' | 'disable';

interface Banner {
  tone: 'success' | 'error' | 'info';
  message: string;
  nextStep?: string;
}

export function AiKnowledgeDetailPage(): ReactNode {
  const api = useApi();
  const { id = '' } = useParams();
  const [entry, setEntry] = useState<AiKnowledgeItem | null>(null);
  const [error, setError] = useState<LoadFailure | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<Banner | null>(null);

  const [confirming, setConfirming] = useState<Confirming>('none');
  const [busy, setBusy] = useState(false);
  const [decisionResult, setDecisionResult] = useState<Banner | null>(null);

  useEffect(() => {
    let cancelled = false;
    setEntry(null);
    setError(null);
    api
      .get<AiKnowledgeItem>(`/admin/ai/knowledge/${id}`)
      .then((loaded) => {
        if (cancelled) return;
        apply(loaded);
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setError(loadFailure(caught));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, id, reloadKey]);

  function apply(loaded: AiKnowledgeItem): void {
    setEntry(loaded);
    setQuestion(loaded.question ?? '');
    setAnswer(loaded.answer ?? '');
    setSourceRef(loaded.source_ref ?? '');
  }

  const changed =
    entry !== null &&
    (question !== (entry.question ?? '') ||
      answer !== (entry.answer ?? '') ||
      sourceRef !== (entry.source_ref ?? ''));

  /** 直した内容を残す（承認の前にも呼ぶ）。 */
  async function saveEdits(): Promise<AiKnowledgeItem> {
    return await api.patch<AiKnowledgeItem>(`/admin/ai/knowledge/${id}`, {
      body: { question, answer, source_ref: sourceRef },
    });
  }

  async function save(): Promise<void> {
    setSaving(true);
    setSaveResult(null);
    try {
      apply(await saveEdits());
      setSaveResult({ tone: 'success', message: '保存しました。' });
    } catch (caught) {
      setSaveResult(failureBanner(caught, '保存できませんでした。'));
    } finally {
      setSaving(false);
    }
  }

  async function approve(): Promise<void> {
    setBusy(true);
    setDecisionResult(null);
    try {
      // 画面で直した内容のまま承認する（直したのに前の内容が承認される、を防ぐ）
      if (changed) await saveEdits();
      const approved = await api.post<AiKnowledgeItem>(`/admin/ai/knowledge/${id}/approve`);
      apply(approved);
      setSaveResult(null);
      setDecisionResult({
        tone: 'success',
        message: '承認しました。AI先輩がこの内容を根拠に、出典つきでお答えします。',
      });
    } catch (caught) {
      setDecisionResult(failureBanner(caught, '承認できませんでした。'));
    } finally {
      setBusy(false);
      setConfirming('none');
    }
  }

  async function disable(): Promise<void> {
    setBusy(true);
    setDecisionResult(null);
    try {
      const disabled = await api.post<AiKnowledgeItem>(`/admin/ai/knowledge/${id}/disable`);
      apply(disabled);
      setSaveResult(null);
      setDecisionResult({
        tone: 'success',
        message: '取り下げました。以後、AI先輩の答えには使われません。',
        // 元の投稿が取り消された行は、戻せない（決定23・issue #245）
        nextStep: isUnsentTaught(disabled)
          ? '元のメッセージが送信取消されているため、この1件はもう一度使うことはできません。'
          : 'この画面の「もう一度使う」で戻せます。',
      });
    } catch (caught) {
      setDecisionResult(failureBanner(caught, 'この操作を行えませんでした。'));
    } finally {
      setBusy(false);
      setConfirming('none');
    }
  }

  if (error) {
    return (
      <article>
        <PageHeader title="AI先輩の答え" />
        <ErrorBlock failure={error} onRetry={() => setReloadKey((n) => n + 1)} />
        <p>
          <Link to="/ai/pending">承認待ちの一覧に戻る</Link>
        </p>
      </article>
    );
  }
  if (!entry) {
    return (
      <article>
        <PageHeader title="AI先輩の答え" />
        <LoadingBlock label="この1件を読み込んでいます…" />
      </article>
    );
  }

  const isDraft = entry.status === 'draft';
  const isActive = entry.status === 'active';
  // 投稿した方が取り消した「教える」部屋の投稿から作った行は、承認できない（決定23・issue #245）
  const unsentSource = isUnsentTaught(entry);
  // 元の投稿が消えた・取り消されたので文を消した行（決定28）。直すことも承認することもできない
  const erased = entry.text_erased === true;
  const kind = pendingKindOf(entry);

  return (
    <article>
      <PageHeader
        title={knowledgeKindLabel(entry.kind)}
        description={`${knowledgeStatusLabel(entry.status)}：${knowledgeStatusNote(entry.status)}`}
        actions={
          // 承認待ちでない答えは、AI先輩の答えの一覧から開いたもの（#329-10）
          entry.status === 'draft' ? (
            <Link className="ui-button ui-button-quiet" to="/ai/pending">
              承認待ちの一覧に戻る
            </Link>
          ) : (
            <Link className="ui-button ui-button-quiet" to="/ai/knowledge">
              AI先輩の答えの一覧に戻る
            </Link>
          )
        }
      />

      {entry.source ? (
        <Card
          title="元のメッセージ"
          description="現場から届いたそのままの中身です（こちらは書き換わりません）。"
        >
          <p>
            <span className="admin-ai-badge">{pendingKindLabel(kind)}</span>
          </p>
          <dl className="admin-facts">
            <dt>どなた・どのトーク</dt>
            <dd>
              {whoAndWhere(entry.source.user_name, entry.source.channel_name, {
                fromOtherSite: entry.source.from_other_site === true,
              })}
            </dd>
            <dt>受け取った日時</dt>
            <dd>{formatDateTime(entry.source.asked_at)}</dd>
          </dl>
          <p className="admin-ai-quote">{sourceTextOf(entry)}</p>
          {sourceNotes(entry).map((note) => (
            <p key={note} className="admin-note">
              {note}
            </p>
          ))}
          {entry.source.attachments && entry.source.attachments.length > 0 ? (
            <SourceAttachments knowledgeId={entry.id} items={entry.source.attachments} />
          ) : null}
        </Card>
      ) : null}

      {erased ? (
        <Card title="質問と答え" description={erasedNote(entry)}>
          <p className="admin-note">{ERASED_NEXT_STEP}</p>
        </Card>
      ) : (
        <FormCard
          title="質問と答え"
          description={
            entry.status === 'draft'
              ? '質問は下書きです（「AI先輩に教える」トークのメッセージからは、最初の一文をそのまま入れています）。現場の方が聞きそうな言い方に直してから承認してください（例：冷蔵庫を階段で運ぶときは？）。'
              : '質問は、現場の方が聞きそうな言い方にしておくと、AI先輩が答えを見つけやすくなります（例：冷蔵庫を階段で運ぶときは？）。'
          }
          submitLabel="保存する"
          submitBusyLabel="保存しています…"
          busy={saving}
          onSubmit={() => void save()}
          footer={saveResult ? <ResultBanner {...saveResult} /> : undefined}
        >
          <label className="ui-field" htmlFor="ai-question">
            <span>質問（どんな聞かれ方に答えるか）</span>
            <textarea
              id="ai-question"
              rows={2}
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
            />
          </label>
          <label className="ui-field" htmlFor="ai-answer">
            <span>答え</span>
            <textarea
              id="ai-answer"
              rows={6}
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
            />
          </label>
          <label className="ui-field" htmlFor="ai-source-ref">
            <span>出典（答えに添えてお返しします）</span>
            <input
              id="ai-source-ref"
              type="text"
              value={sourceRef}
              onChange={(e) => setSourceRef(e.target.value)}
            />
          </label>
          {changed ? (
            <p className="admin-note">
              まだ保存していません。「保存する」か「承認する」を押すと、この内容で残ります。
            </p>
          ) : null}
        </FormCard>
      )}

      <Card title="この内容をどうしますか" description={knowledgeStatusNote(entry.status)}>
        {decisionResult ? <ResultBanner {...decisionResult} /> : null}
        {unsentSource && !isActive && !erased ? <p className="admin-note">{UNSENT_NOTE}</p> : null}
        <div className="ui-form-actions">
          {isActive || unsentSource || erased ? null : (
            <Button
              variant="primary"
              busy={busy && confirming === 'approve'}
              onClick={() => setConfirming('approve')}
            >
              {isDraft ? '承認する' : 'もう一度使う'}
            </Button>
          )}
          {entry.status === 'disabled' ? null : (
            // 「承認する」と見分けがつくよう、控えの見た目にする（総点検 M17）。
            // 取り下げても「もう一度使う」で戻せるので、危険の赤にはしない
            <Button
              variant="secondary"
              busy={busy && confirming === 'disable'}
              onClick={() => setConfirming('disable')}
            >
              取り下げる
            </Button>
          )}
          <Link className="ui-button ui-button-quiet" to="/ai/pending">
            {/* 選べる操作が無いときは「後で」ではなく、戻ることをそのまま書く */}
            {(unsentSource || erased) && entry.status === 'disabled' ? '一覧に戻る' : '後で'}
          </Link>
        </div>
        {entry.approved_at ? (
          <p className="admin-note">承認した日時：{formatDateTime(entry.approved_at)}</p>
        ) : null}
      </Card>

      <ConfirmDialog
        open={confirming === 'approve'}
        title={isDraft ? 'この内容を承認します' : 'この内容をもう一度使います'}
        message={`承認すると、AI先輩がこの1件を根拠に、出典つきでお答えするようになります。質問「${question}」`}
        reversibility="この画面の「取り下げる」で、いつでも使うのをやめられます。"
        reversible
        tone="primary"
        confirmLabel={isDraft ? '承認する' : 'もう一度使う'}
        busy={busy}
        onConfirm={() => void approve()}
        onCancel={() => setConfirming('none')}
      />

      <ConfirmDialog
        open={confirming === 'disable'}
        title="この1件を取り下げます"
        message="以後、AI先輩の答えには使われません。記録は残るので、あとから見返せます。"
        reversibility={
          unsentSource
            ? '元のメッセージが送信取消されているため、あとから「もう一度使う」で戻すことはできません。'
            : 'この画面の「もう一度使う」で戻せます。'
        }
        reversible={!unsentSource}
        confirmLabel="取り下げる"
        busy={busy}
        onConfirm={() => void disable()}
        onCancel={() => setConfirming('none')}
      />
    </article>
  );
}

/** うまくいかなかったときの帯（理由と、次にすることを必ず添える）。 */
/** 取り消した投稿から作った行を開いたときの案内（承認できない理由と、次にできること）。 */
const UNSENT_NOTE =
  '送った方が元のメッセージを送信取消したため、この下書きは承認できません（送信取消したメッセージはAI先輩の材料にしません）。' +
  '同じことを覚えさせたいときは、あらためて「AI先輩に教える」トークに送っていただくか、「公式資料を登録する」から登録してください。';

/** 文を消した行を開いたときの説明（なぜ文が無いのか・決定28）。 */
function erasedNote(entry: AiKnowledgeItem): string {
  return entry.source?.unsent === true
    ? '送った方が元のメッセージを送信取消したため、承認される前だったこの下書きの質問と答えも消しました。'
    : '元のメッセージが保存期間を過ぎたかトークごと削除されて消えたため、承認される前だったこの下書きの質問と答えも消しました。';
}

/** 文を消した行でできること（次の手）。 */
const ERASED_NEXT_STEP =
  'この1件は、直すことも承認することもできません。同じことを覚えさせたいときは、あらためて「AI先輩に教える」トークに送っていただくか、「公式資料を登録する」から登録してください。';

/** 投稿した方が取り消した「教える」部屋の投稿から作った行か（承認できない・決定23・issue #245）。 */
function isUnsentTaught(entry: AiKnowledgeItem): boolean {
  return entry.source?.origin === 'taught' && entry.source.unsent === true;
}

function failureBanner(caught: unknown, headline: string): Banner {
  const failure = loadFailure(caught);
  return {
    tone: 'error',
    message: withReason(headline, failure.message),
    // 権限が無いときに「時間をおいて」と出さない（総点検 J-08）
    nextStep: nextStepFor(failure.status, failure.retryable).nextStep,
  };
}

/**
 * 元の投稿に添えられた写真など（決定26・issue #255）。
 *
 * 写真は小さな写真をその場で出し、「大きく開く」で元の大きさを別の画面で開く。
 * 写真以外（動画・ファイル）は、押したときにだけ開く（動画を勝手に読み込まない・docs/01 13-5）。
 * 見るためのリンクは短い間だけ使えるもので、そのつど発行する。
 */
function SourceAttachments({
  knowledgeId,
  items,
}: {
  knowledgeId: string;
  items: AiSourceAttachment[];
}): ReactNode {
  const api = useApi();
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const photos = items.filter((item) => item.content_type.startsWith('image/'));
    Promise.all(
      photos.map(async (item) => {
        const link = await api.get<AiAttachmentLink>(
          `/admin/ai/knowledge/${knowledgeId}/attachments/${item.id}`,
          { query: item.has_thumbnail ? { variant: 'thumbnail' } : {} },
        );
        return [item.id, link.url] as const;
      }),
    )
      .then((pairs) => {
        if (!cancelled) setPreviews(Object.fromEntries(pairs));
      })
      .catch((caught: unknown) => {
        if (!cancelled) {
          setFailed(caught instanceof ApiError ? caught.message : messageForStatus(0));
        }
      });
    return () => {
      cancelled = true;
    };
  }, [api, knowledgeId, items]);

  async function open(item: AiSourceAttachment): Promise<void> {
    setFailed(null);
    try {
      const link = await api.get<AiAttachmentLink>(
        `/admin/ai/knowledge/${knowledgeId}/attachments/${item.id}`,
      );
      window.open(link.url, '_blank', 'noopener');
    } catch (caught) {
      setFailed(caught instanceof ApiError ? caught.message : messageForStatus(0));
    }
  }

  return (
    <div>
      <p className="admin-note">
        添えられた写真など（承認するときの参考です。AI先輩が答えに使うのは文字だけです）
      </p>
      <ul className="admin-ai-attachments">
        {items.map((item, index) => {
          const isPhoto = item.content_type.startsWith('image/');
          const label = isPhoto
            ? `写真 ${index + 1}`
            : item.content_type.startsWith('video/')
              ? `動画 ${index + 1}`
              : `ファイル ${index + 1}`;
          const preview = previews[item.id];
          return (
            <li key={item.id}>
              {isPhoto && preview ? (
                <img src={preview} alt={`元のメッセージの${label}`} width={160} />
              ) : (
                <span>{label}</span>
              )}
              <Button variant="secondary" compact onClick={() => void open(item)}>
                {isPhoto ? '大きく開く' : '開く'}
              </Button>
            </li>
          );
        })}
      </ul>
      {failed ? (
        <ResultBanner
          tone="error"
          message={withReason('開けませんでした', failed)}
          nextStep="少し時間をおいて、もう一度お試しください。"
        />
      ) : null}
    </div>
  );
}
